import classNames from 'classnames';
import startCase from 'lodash/startCase';
import type React from 'react';
import { useMemo } from 'react';

import { Loading } from '@revelio/core';
import { FormatType, getFormatter } from '@revelio/replots';
import { Tag } from '@revelio/ui';

import { useTrialUserBlurLogic } from '../../../hooks/useTrialUserBlurLogic';
import { normalizeProfileUrl } from '../../../utils/normalizeProfileUrl';
import BlurredCell from '../../BlurredCell';
import styles from './FullProfileInfo.module.css';
import { ProfileBadge } from './ProfileBadge';
import type { FullProfileInfoData, SalaryRange } from './types';

const salaryFormatter = getFormatter(FormatType.CURRENCY);

type NameParts = {
  firstName: string;
  lastName: string;
  fullName: string;
};

const getHeaderNameParts = (
  header: FullProfileInfoData['header']
): NameParts => {
  const providedFirstName = header.firstName?.trim() ?? '';
  const providedLastName = header.lastName?.trim() ?? '';

  if (providedFirstName || providedLastName) {
    return {
      firstName: providedFirstName,
      lastName: providedLastName,
      fullName: [providedFirstName, providedLastName].filter(Boolean).join(' '),
    };
  }

  const trimmedName = header.name.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '', fullName: '' };
  }

  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length === 1) {
    return {
      firstName: nameParts[0] ?? '',
      lastName: '',
      fullName: trimmedName,
    };
  }

  return {
    firstName: nameParts.slice(0, -1).join(' '),
    lastName: nameParts[nameParts.length - 1] ?? '',
    fullName: trimmedName,
  };
};

/** Trial users see masked placeholders over the raw contact fields. Shared by
 *  both layouts so the masking rules can't drift between them. */
const getMaskedHeader = (
  header: FullProfileInfoData['header'],
  isTrialUser: boolean
): FullProfileInfoData['header'] => {
  if (!isTrialUser) {
    return header;
  }

  const maskedValues = header.maskedValues ?? {};
  return {
    ...header,
    lastName: maskedValues.lastName ?? header.lastName ?? '',
    workEmail: maskedValues.workEmail ?? header.workEmail,
    personalEmail: maskedValues.personalEmail ?? header.personalEmail,
    phone: maskedValues.phone ?? header.phone,
    profileUrl: maskedValues.profileUrl ?? header.profileUrl,
  };
};

const formatSalaryRange = ({ min, max }: SalaryRange): string => {
  if (min === max) {
    return salaryFormatter(min);
  }

  return `${salaryFormatter(min)} - ${salaryFormatter(max)}`;
};

interface FullProfileInfoProps {
  data: FullProfileInfoData;
}

export const FullProfileInfoLoading = () => {
  return (
    <div
      className={styles.loadingContainer}
      data-testid="full-profile-info-loading"
    >
      <Loading size="lg" />
    </div>
  );
};

const hasSalary = (salary: SalaryRange): boolean =>
  salary.min > 0 || salary.max > 0;

/**
 * Dashboard 2026 profile popout. Native elements + CSS-module classes keep
 * colors token-driven so they flip with body[data-theme].
 */
export const FullProfileInfo = ({ data }: FullProfileInfoProps) => {
  const { isTrialUser, showBlurToast } = useTrialUserBlurLogic();
  const userKey = data.originalUserId ?? data.userId;
  const maskedHeader = useMemo(
    () => getMaskedHeader(data.header, isTrialUser),
    [data.header, isTrialUser]
  );

  const nameParts = getHeaderNameParts(maskedHeader);
  const shouldBlurLastName = isTrialUser && nameParts.lastName.length > 0;

  const renderContact = (
    value: string,
    fieldKey: string,
    encryptedValue: string | undefined,
    href?: string
  ): React.ReactNode => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (isTrialUser) {
      return (
        <span className={styles.link}>
          <BlurredCell
            value={trimmed}
            encryptedValue={encryptedValue}
            onMouseEnter={showBlurToast}
            allowUnblur={false}
            isCompact={false}
            userKey={userKey}
            fieldKey={fieldKey}
          />
        </span>
      );
    }
    if (href) {
      return (
        <a className={styles.link} href={href}>
          {trimmed}
        </a>
      );
    }
    return <span className={styles.link}>{trimmed}</span>;
  };

  const renderProfileUrl = (): React.ReactNode => {
    const trimmed = maskedHeader.profileUrl.trim();
    if (!trimmed) {
      return null;
    }
    const normalized = normalizeProfileUrl(trimmed);
    if (!normalized) {
      return null;
    }
    if (isTrialUser) {
      return (
        <span className={styles.link}>
          <BlurredCell
            value={normalized.displayValue}
            encryptedValue={maskedHeader.originalValues?.profileUrl}
            onMouseEnter={showBlurToast}
            allowUnblur={false}
            isCompact={false}
            userKey={userKey}
            fieldKey="linkedin_url"
          />
        </span>
      );
    }
    return (
      <a
        className={styles.link}
        href={normalized.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {normalized.displayValue}
      </a>
    );
  };

  const skills = (data.skills || []).filter(
    (skill) => skill && typeof skill === 'string' && skill.trim().length > 0
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.info}>
            {shouldBlurLastName ? (
              <div className={classNames(styles.name, styles.nameRow)}>
                {nameParts.firstName && <span>{nameParts.firstName}</span>}
                <span>
                  <BlurredCell
                    value={nameParts.lastName}
                    encryptedValue={maskedHeader.originalValues?.lastName}
                    onMouseEnter={showBlurToast}
                    allowUnblur={false}
                    isCompact={false}
                    userKey={userKey}
                    fieldKey="last_name"
                  />
                </span>
              </div>
            ) : (
              <p className={styles.name}>{nameParts.fullName}</p>
            )}
            {maskedHeader.role && (
              <p className={styles.subtle}>{maskedHeader.role}</p>
            )}
            {maskedHeader.location && (
              <p className={styles.subtle}>{maskedHeader.location}</p>
            )}
          </div>
          <div className={styles.contact}>
            {renderContact(
              maskedHeader.workEmail,
              'email',
              maskedHeader.originalValues?.workEmail,
              `mailto:${maskedHeader.workEmail.trim()}`
            )}
            {renderContact(
              maskedHeader.personalEmail,
              'personal_email',
              maskedHeader.originalValues?.personalEmail,
              `mailto:${maskedHeader.personalEmail.trim()}`
            )}
            {renderContact(
              maskedHeader.phone,
              'phone_number',
              maskedHeader.originalValues?.phone
            )}
            {renderProfileUrl()}
          </div>
        </div>

        <div className={styles.metaCard}>
          {hasSalary(data.metadata.salary) && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Salary:</span>
              <span className={styles.salary}>
                {formatSalaryRange(data.metadata.salary)}
              </span>
            </div>
          )}
          {data.metadata.prestige && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Prestige:</span>
              <ProfileBadge value={data.metadata.prestige} />
            </div>
          )}
          {data.metadata.flightRisk && (
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Flight Risk:</span>
              <ProfileBadge value={data.metadata.flightRisk} />
            </div>
          )}
        </div>
      </div>

      {/* Work Experience */}
      {data.workExperience.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Work Experience</p>
          <div className={styles.entries}>
            {data.workExperience.map((entry, index) => (
              <div className={styles.entry} key={`work-experience-${index}`}>
                <div className={styles.entryInfo}>
                  <span className={styles.subtle}>
                    {entry.startDate} - {entry.endDate}
                  </span>
                  <span className={styles.entryRole}>{entry.role}</span>
                  {entry.positionLocation && (
                    <span className={styles.subtle}>
                      {entry.positionLocation}
                    </span>
                  )}
                </div>
                {(entry.companyPrestige || hasSalary(entry.salary)) && (
                  <div className={styles.metaCard}>
                    {entry.companyPrestige && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaLabel}>
                          Company Prestige:
                        </span>
                        <ProfileBadge value={entry.companyPrestige} />
                      </div>
                    )}
                    {hasSalary(entry.salary) && (
                      <div className={styles.metaRow}>
                        <span className={styles.metaLabel}>Salary:</span>
                        <span className={styles.salary}>
                          {formatSalaryRange(entry.salary)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education */}
      {data.education.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Education</p>
          <div className={styles.entries}>
            {data.education.map((entry, index) => (
              <div className={styles.entry} key={`education-${index}`}>
                <div className={styles.entryInfo}>
                  <span className={styles.subtle}>
                    {entry.startDate} - {entry.endDate || 'Present'}
                  </span>
                  <span className={styles.entryRole}>{entry.institution}</span>
                  {entry.degree && (
                    <span className={styles.subtle}>{entry.degree}</span>
                  )}
                  {entry.fieldOfStudy && (
                    <span className={styles.subtle}>{entry.fieldOfStudy}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Skills */}
      {skills.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Skills</p>
          <div className={styles.skills}>
            {skills.map((skill, index) => (
              <Tag
                key={`skill-${index}`}
                size="sm"
                colorScheme="gray"
                variant="subtle"
              >
                {startCase(String(skill))}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Publications */}
      {data.publications.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Publications</p>
          <div className={styles.entries}>
            {data.publications.map((entry, index) => (
              <div className={styles.entryInfo} key={`publication-${index}`}>
                <span className={styles.subtle}>{entry.date}</span>
                <span className={styles.entryRole}>{entry.title}</span>
                {entry.source && (
                  <span className={styles.subtle}>{entry.source}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Languages */}
      {data.languages.length > 0 && (
        <div className={styles.section}>
          <p className={styles.sectionTitle}>Languages</p>
          <div className={styles.entryInfo}>
            {data.languages.map((entry, index) => (
              <span className={styles.subtle} key={`language-${index}`}>
                {entry.language} - {entry.proficiency}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
